// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { Box, Grid, Typography, Popover, Chip, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import MonacoEditor, { loader } from '@monaco-editor/react';

import UpdateSourceChip from '../../Tables/UpdateSourceChip';
import TextEditor from '../../ModelInventoryAndAssociation/TextEditor/TextEditor';
import { draftRawToTipTapHtml } from '../../ModelInventoryAndAssociation/TextEditor/utils/draftTipTapConverter';
import { registerCustomPython } from '../../../Monaco/monacoCustomPython';
import { registerFormulaAttributeHoverProvider } from '../../../Monaco/monacoHoverProviders';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';

loader.config({ paths: { vs: '/monaco' } });

/**
 * @function getTipTapPlainText
 * @description Extracts plain text recursively from a TipTap document for description-length and empty-state checks.
 * @returns {string} Plain text without formatting or hyperlink metadata.
 */
const getTipTapPlainText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  return (node.content || []).map(getTipTapPlainText).join(' ');
};

/**
 * @component AttributeInfoPopover
 * @description Displays attribute metadata, derived formulas, and a rich description with expandable content and hyperlinks.
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the popover is open
 * @param {HTMLElement} props.anchorEl - The anchor element for the popover
 * @param {Function} props.onClose - Function to handle popover close
 * @param {Object} props.attribute - The attribute data
 * @returns {React.ReactElement} The AttributeInfoPopover component
 * @example <AttributeInfoPopover open={open} anchorEl={anchorEl} onClose={handleClose} attribute={attribute} />
 */
const AttributeInfoPopover = ({
  open,
  anchorEl,
  onClose,
  attribute,
  attributeSourceMapping,
  loading,
  attributeList = EMPTY_ARRAY,
}) => {
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const theme = useTheme();
  // Ref to store the Monaco hover provider disposable
  const hoverProviderRef = useRef(null);

  const templateUsage = [];

  const attDesc =
    attribute?.attribute_origin === 'common'
      ? 'This is system generated attribute.'
      : attribute?.description;

  const plainText = useMemo(() => {
    if (!attDesc) return '';
    try {
      const parsed = JSON.parse(attDesc);
      if (parsed?.type === 'doc') {
        return getTipTapPlainText(parsed);
      }
      if (Array.isArray(parsed?.blocks)) {
        return parsed.blocks.map((b) => b.text).join('\n');
      }
    } catch {
      // If parsing fails, fallback to HTML parsing
    }
    const temp = document.createElement('div');
    temp.innerHTML = attDesc;
    return temp.textContent || attDesc;
  }, [attDesc]);

  // MAX characters before showing Read More
  const MAX_CHARS = 200;

  const fullDesc = plainText || '';
  const showReadMoreButton = fullDesc.length > MAX_CHARS;
  const renderedDesc = useMemo(() => {
    if (!showReadMoreButton || showFullDescription) {
      return attDesc;
    }
    let parsed = attDesc;
    try {
      parsed = JSON.parse(attDesc);
    } catch {
      parsed = attDesc;
    }
    let remaining = MAX_CHARS;
    if (parsed?.type === 'doc') {
      const truncateNode = (node) => {
        if (remaining <= 0) {
          return null;
        }
        if (node?.type === 'text') {
          const text = node?.text?.slice(0, remaining) || '';
          remaining -= text.length;
          return { ...node, text };
        }
        if (!node?.content) {
          const label = node?.attrs?.label || node?.attrs?.name || '';
          const text = label.slice(0, remaining);
          remaining -= text.length;
          return text.length < label.length ? { type: 'text', text } : node;
        }
        return {
          ...node,
          content: node.content.map(truncateNode).filter(Boolean),
        };
      };
      const truncated = truncateNode(parsed);
      const appendEllipsis = (node) => {
        if (['paragraph', 'heading', 'codeBlock'].includes(node?.type)) {
          node.content.push({ type: 'text', text: '...' });
          return true;
        }
        return [...(node?.content || [])].reverse().some(appendEllipsis);
      };
      appendEllipsis(truncated);
      return truncated;
    }
    const container = document.createElement('div');
    container.innerHTML = Array.isArray(parsed?.blocks)
      ? draftRawToTipTapHtml(parsed)
      : attDesc || '';
    const truncateElement = (element) => {
      Array.from(element.childNodes).forEach((node) => {
        if (remaining <= 0) {
          node.remove();
        } else if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.slice(0, remaining);
          node.replaceWith(document.createTextNode(text));
          remaining -= text.length;
        } else {
          truncateElement(node);
        }
      });
    };
    truncateElement(container);
    const lastBlock = Array.from(
      container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, pre, li')
    ).pop();
    (lastBlock || container).append(document.createTextNode('...'));
    return container.innerHTML;
  }, [attDesc, showReadMoreButton, showFullDescription]);
  const isDescriptionEmpty = plainText.trim().length === 0;

  useEffect(() => {
    setShowFullDescription(false);
  }, [attribute?.id, attDesc, open]);

  //Extract source details based on current attribute ID
  const sourceDetails = useMemo(() => {
    const attributeId = attribute?.id;
    return attributeSourceMapping?.[attributeId] || {};
  }, [attributeSourceMapping, attribute?.id]);

  const { source_type, template_name, automation_id, attribute_mapping_id } =
    sourceDetails;

  // Handle popover close and reset template state
  const handleClose = useCallback(() => {
    setShowAllTemplates(false);
    setShowFullDescription(false);
    onClose();
  }, [onClose, setShowAllTemplates]);

  // Handle show more templates
  const handleShowMoreTemplates = useCallback(() => {
    setShowAllTemplates(true);
  }, [setShowAllTemplates]);

  const displayedTemplates = useMemo(() => {
    return showAllTemplates ? templateUsage : templateUsage.slice(0, 5);
  }, [showAllTemplates, templateUsage]);

  const hasMoreTemplates = useMemo(() => {
    return templateUsage.length > 5;
  }, [templateUsage.length]);

  /**
   * @function renderUpdateSourceCell
   * @description Renders the attribute's update source, loading label, or fallback placeholder.
   * @returns {JSX.Element} The update-source display.
   */
  const renderUpdateSourceCell = () => {
    const attributeId = attribute?.id;
    const sourceType = attributeSourceMapping?.[attributeId]?.source_type;
    if (loading) {
      return <UpdateSourceChip label="Loading..." />;
    }
    if (sourceType) {
      return <UpdateSourceChip label={sourceType} />;
    }
    return <Typography variant="body8">-</Typography>;
  };

  /**
   * @function handleToggleFullDescription
   * @description Toggles between the full rich description and its formatting-preserving preview.
   */
  const handleToggleFullDescription = useCallback(() => {
    setShowFullDescription((prev) => !prev);
  }, [setShowFullDescription]);

  /**
   * @function handleEditorBeforeMount
   * @description Registers the derived-formula theme and attribute hover provider, disposing any previous provider first.
   */
  const handleEditorBeforeMount = useCallback(
    (monaco) => {
      const derivedFormulaTheme =
        theme?.palette?.monacoEditor?.derivedFormulaEditor;
      // Dispose previous hover provider to prevent duplication
      if (hoverProviderRef.current) {
        hoverProviderRef.current.dispose();
        hoverProviderRef.current = null;
      }
      registerCustomPython(monaco, {
        tokenColors: derivedFormulaTheme?.tokenColors,
        colors: derivedFormulaTheme?.colors,
      });
      monaco?.editor?.setTheme('customPythonTheme');
      // Store the disposable in the ref so we can clean up next time
      hoverProviderRef.current = registerFormulaAttributeHoverProvider(
        monaco,
        attributeList
      );
    },
    [theme, attributeList]
  );

  // Clean up hover provider on unmount
  useEffect(() => {
    return () => {
      if (hoverProviderRef.current) {
        hoverProviderRef.current.dispose();
        hoverProviderRef.current = null;
      }
    };
  }, []);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          maxHeight: 400,
          width: 450,
        },
      }}
    >
      <Box
        component="section"
        aria-label="Attribute details"
        sx={{
          p: 2,
          maxHeight: 400,
          overflowY: 'auto',
        }}
      >
        {/* First row: Data Type and Update Source */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid container item xs={6} rowGap={1}>
            <Grid item xs={12}>
              <Typography variant="body4">Data Type</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body8">
                {attribute?.data_type || 'Not specified'}
              </Typography>
            </Grid>
          </Grid>
          <Grid container item xs={6} rowGap={1}>
            <Grid item xs={12}>
              <Typography variant="body4">Update Source</Typography>
            </Grid>
            <Grid item xs={12}>
              {renderUpdateSourceCell()}
            </Grid>
          </Grid>
        </Grid>

        {/* Second Row: Source Template only for Calculated , Automation and Attribute Mapping Source Type and ID only for Automation and Attribute Mapping */}
        {(source_type === 'Automation' ||
          source_type === 'Mapping' ||
          source_type === 'Calculated') && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Source Template */}
            <Grid container item xs={6} rowGap={1}>
              <Grid item xs={12}>
                <Typography variant="body4">Source Template</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body8">{template_name || '-'}</Typography>
              </Grid>
            </Grid>

            {/* ID Column */}
            {(source_type === 'Automation' || source_type === 'Mapping') && (
              <Grid container item xs={6} rowGap={1}>
                <Grid item xs={12}>
                  <Typography variant="body4">
                    {source_type === 'Mapping' ? 'Mapping ID' : 'Automation ID'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body8">
                    {source_type === 'Mapping'
                      ? attribute_mapping_id || '-'
                      : automation_id || '-'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}

        {attribute?.is_derived && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid container item xs={12} rowGap={1}>
              <Grid item xs={12}>
                <Typography variant="body4">Derived Formula</Typography>
              </Grid>
              <Grid item xs={12}>
                {attribute?.derived_formula ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <MonacoEditor
                      height="150px"
                      language="custom-python"
                      theme="customPythonTheme"
                      value={attribute?.derived_formula || ''}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        readOnly: true,
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        lineNumbers: 'off',
                        folding: false,
                        renderLineHighlight: 'none',
                        scrollbar: {
                          vertical: 'auto',
                          horizontal: 'auto',
                        },
                      }}
                      beforeMount={handleEditorBeforeMount}
                    />
                  </Box>
                ) : (
                  <Typography variant="body8">-</Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid container item xs={12} rowGap={1}>
            <Grid item xs={12}>
              <Typography variant="body4">Description</Typography>
            </Grid>
            <Grid item xs={12}>
              <Box>
                {!isDescriptionEmpty ? (
                  <TextEditor content={renderedDesc} readOnly />
                ) : (
                  <Typography>No description available.</Typography>
                )}
              </Box>
              {showReadMoreButton && (
                <Button
                  size="small"
                  onClick={handleToggleFullDescription}
                  sx={{ mt: 1 }}
                >
                  {showFullDescription ? 'Show less' : 'Read more'}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* Second container: Template Usage */}
        {templateUsage.length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2">Templates</Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: hasMoreTemplates && !showAllTemplates ? 1 : 0,
                }}
              >
                {displayedTemplates.map((template) => (
                  <Chip
                    key={template}
                    label={template}
                    color="primary"
                    variant="primary"
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
            {hasMoreTemplates && !showAllTemplates && (
              <Grid item xs={12}>
                <Button size="small" onClick={handleShowMoreTemplates}>
                  Show More ({templateUsage.length - 5} more)
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Popover>
  );
};

AttributeInfoPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.instanceOf(Element),
  onClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data_type: PropTypes.string,
    attribute_origin: PropTypes.string,
    template_usage: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    is_derived: PropTypes.bool,
    derived_formula: PropTypes.string,
  }),
  attributeSourceMapping: PropTypes.objectOf(
    PropTypes.shape({
      source_type: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  attributeList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

export default AttributeInfoPopover;
